import { createAction } from '@reduxjs/toolkit';
const createActionWithServerId = <P = undefined, T extends string = string>(type: T) => createAction<P extends undefined ? { serverId: string} : { serverId: string, data: P }>(type);

export const socketConnect = createAction('socket_connect');
export const socketDisconnect = createAction('socket_disconnect');

// CONTEXT
export interface SocketGetContextParams {
  refresh: boolean,
}
export const socketGetContext = createActionWithServerId<SocketGetContextParams>('socket_get_context');

// STATS
export const socketSubscribeStats = createActionWithServerId('socket_subscribe_stats');
export const socketUnsubscribeStats = createActionWithServerId('socket_unsubscribe_stats');
export const socketGetStats = createActionWithServerId('socket_get_stats');

// SESSIONS
export enum LiveSessionStatus {
  INITIALIZED = 'initialized',
  RUNNING = 'running',
  TERMINATED = 'terminated',
  CANCEL = 'cancel',
}
export interface SocketGetSessionParams {
  serverId?: string,
  vhost?: string,
  app?: string,
  stream?: string,
  search?: string,
  limit?: number,
  skip?: number,
  sortField?: string,
  sort?: 1 | -1,
  status?: LiveSessionStatus | LiveSessionStatus[],
  onlyWithRecords?: boolean,
  onlyWithRedirections?: boolean,
  // Records only: statuses of `subtitles.status`, plus `none` for the records without any.
  subtitlesStatus?: string[],
}
export const socketGetLiveSessions = createAction<SocketGetSessionParams>('socket_get_live_sessions');
export const socketGetRecordSessions = createAction<SocketGetSessionParams>('socket_get_record_sessions');

// SUBTITLES & MEDIA EVENTS — per-server commands (the records live on their server).
export const socketGetSubtitlesState = createActionWithServerId('socket_get_subtitles_state');
export interface SocketSubtitlesRecordParams {
  recordSessionId: string,
  // A forced transcription from scratch; with `now`, one that runs at once.
  force?: boolean,
  // The transcript on disk delivered again to the record's s3-file-storage uploads.
  deliver?: boolean,
  // Run past the gate: the window always, the busy check too when `busy`.
  now?: { busy?: boolean },
}
export const socketSubtitlesRecord = createActionWithServerId<SocketSubtitlesRecordParams>('socket_subtitles_record');
export const socketSubtitlesRunAll = createActionWithServerId('socket_subtitles_run_all');
export const socketSubtitlesStop = createActionWithServerId('socket_subtitles_stop');
export interface SocketGetMediaEventsParams {
  recordSessionId: string,
}
export const socketGetMediaEvents = createActionWithServerId<SocketGetMediaEventsParams>('socket_get_media_events');


export interface SocketPullStreamParams {
  appName: string,
  streamName: string,
  url: string,
  force?: boolean,
  persistent?: boolean,
  noInputFailoverTimeoutMs?: number,
  unusedStreamDeletionTimeoutMs?: number,
}
export const socketPullStream = createActionWithServerId<SocketPullStreamParams>('socket_pull_stream');

export interface SocketStopStreamParams {
  liveSession?: string,
  app?: string,
  stream?: string,
}
export const socketStopStream = createActionWithServerId<SocketStopStreamParams>('socket_stop_stream');

export interface SocketStartRecordParams {
  liveSession: string,
  tracks?: string[],
}
export const socketStartRecord = createActionWithServerId<SocketStartRecordParams>('socket_start_record');

export interface SocketStopRecordParams {
  recordSession: string,
}
export const socketStopRecord = createActionWithServerId<SocketStopRecordParams>('socket_stop_record');

export interface SocketDeleteRecordParams {
  recordSession: string,
}
export const socketDeleteRecord = createActionWithServerId<SocketStopRecordParams>('socket_delete_record');

export interface SocketBuildRecordParams {
  recordSession: string,
  ffmpegCmd?: string,
  immediate?: boolean,
}
export const socketBuildRecord = createActionWithServerId<SocketBuildRecordParams>('socket_build_record');


export interface SocketAddRedirectionParams {
  liveSession: string,
  url: string,
  key?: string,
  tracks?: string[],
}
export const socketAddRedirection = createActionWithServerId<SocketAddRedirectionParams>('socket_add_redirection');

export interface SocketUploadRecordParams {
  uploadProfile: string,
  recordSession: string,
}
export const socketUploadRecord = createActionWithServerId<SocketUploadRecordParams>('socket_upload_record');

export interface SocketRestartUploadRecordParams {
  uploadSession: string,
  immediate?: boolean;
}
export const socketRestartUploadRecord = createActionWithServerId<SocketRestartUploadRecordParams>('socket_restart_upload_record');


export interface SocketDeleteRedirectionParams {
  redirectionSession: string,
}
export const socketDeleteRedirection = createActionWithServerId<SocketDeleteRedirectionParams>('socket_delete_redirection');


// LOGS
export const socketSubscribeLogs = createActionWithServerId('socket_subscribe_logs');
export const socketUnsubscribeLogs = createActionWithServerId('socket_unsubscribe_logs');
export const socketGetLogs = createActionWithServerId('socket_get_logs');

// OME APPLICATIONS (managed app config)
export interface SocketUpsertOmeApplicationParams {
  name: string,
  config: any,
  notes?: string,
  // Admission-only (kept off config/XML): the default stream profile a bare
  // identity-only URL on this app resolves to, and whether a bare URL with no
  // default is rejected instead of published plainly.
  defaultProfile?: string,
  requireDefaultProfile?: boolean,
  author?: string,
}
export interface SocketApplyOmeApplicationParams {
  name: string,
  include?: string[],
  author?: string,
}
export interface SocketDeleteOmeApplicationParams {
  name: string,
  author?: string,
}
export interface SocketLintOmeApplicationParams {
  config: any,
}
export interface SocketRetryImportOmeApplicationParams {
  name: string,
  author?: string,
}
export interface SocketGetOmeApplicationRevisionsParams {
  name: string,
  limit?: number,
}
export const socketGetOmeApplications = createAction('socket_get_ome_applications');
export const socketUpsertOmeApplication = createAction<SocketUpsertOmeApplicationParams>('socket_upsert_ome_application');

// STREAM PROFILES (Live ABR named profiles) — never bare "profiles" in UI
// (collides with VOD upload profiles).
export interface SocketUpsertStreamProfileParams {
  name: string,
  description?: string,
  roles: string[],
  push?: any,
  pull?: any,
  author?: string,
}
export interface SocketDeleteStreamProfileParams {
  name: string,
  author?: string,
}
export interface SocketLintStreamProfileParams {
  name?: string,
  description?: string,
  roles?: string[],
  push?: any,
  pull?: any,
}
export interface SocketGetStreamProfileRevisionsParams {
  name: string,
  limit?: number,
}
export const socketGetStreamProfiles = createAction('socket_get_stream_profiles');
export const socketUpsertStreamProfile = createAction<SocketUpsertStreamProfileParams>('socket_upsert_stream_profile');
export const socketDeleteStreamProfile = createAction<SocketDeleteStreamProfileParams>('socket_delete_stream_profile');
export const socketLintStreamProfile = createAction<SocketLintStreamProfileParams>('socket_lint_stream_profile');
export const socketGetStreamProfileRevisions = createAction<SocketGetStreamProfileRevisionsParams>('socket_get_stream_profile_revisions');
export const socketApplyOmeApplication = createAction<SocketApplyOmeApplicationParams>('socket_apply_ome_application');
export const socketDeleteOmeApplication = createAction<SocketDeleteOmeApplicationParams>('socket_delete_ome_application');
export const socketLintOmeApplication = createAction<SocketLintOmeApplicationParams>('socket_lint_ome_application');
export const socketRetryImportOmeApplication = createAction<SocketRetryImportOmeApplicationParams>('socket_retry_import_ome_application');
export const socketGetOmeApplicationRevisions = createAction<SocketGetOmeApplicationRevisionsParams>('socket_get_ome_application_revisions');

export default {
  socketConnect,
  socketDisconnect,
  socketGetContext,
  socketSubscribeStats,
  socketUnsubscribeStats,
  socketGetStats,
  socketPullStream,
  socketStopStream,
  socketStartRecord,
  socketStopRecord,
  socketDeleteRecord,
  socketBuildRecord,
  socketAddRedirection,
  socketUploadRecord,
  socketRestartUploadRecord,
  socketDeleteRedirection,
  socketGetLiveSessions,
  socketGetRecordSessions,
  socketGetSubtitlesState,
  socketSubtitlesRecord,
  socketSubtitlesRunAll,
  socketSubtitlesStop,
  socketGetMediaEvents,
  socketSubscribeLogs,
  socketUnsubscribeLogs,
  socketGetLogs,
  socketGetOmeApplications,
  socketUpsertOmeApplication,
  socketApplyOmeApplication,
  socketDeleteOmeApplication,
  socketLintOmeApplication,
  socketRetryImportOmeApplication,
  socketGetOmeApplicationRevisions,
  socketGetStreamProfiles,
  socketUpsertStreamProfile,
  socketDeleteStreamProfile,
  socketLintStreamProfile,
  socketGetStreamProfileRevisions,
};
